/**
 * ApiClient — mirrors Flutter `core/api/api_client.dart`.
 *
 * Three interceptors stacked the same way as Dio:
 *   1. AuthInterceptor — JWT Bearer injection + proactive refresh
 *   2. RefreshOn401   — single-flight refresh + retry on auth errors
 *   3. ErrorMapper    — backend error shape → typed exceptions
 *
 * The shape of `tokens` returned by `/api/auth/verify-code` and
 * `/api/auth/refresh-token` is `{ access_token, refresh_token }`. The
 * Flutter client decodes the JWT to read its `exp` and refreshes 60s before
 * expiry; we do the same for parity (saves a 401 round-trip on slow networks).
 */
import axios, {
  AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios';
import { tokenStorage } from './tokenStorage';
import {
  ApiException,
  AuthException,
  NetworkException,
  ServerException,
  ValidationException,
} from '@/types/exceptions';

const BASE_URL = (import.meta.env.VITE_API_URL as string) || '';
const REFRESH_LEAD_MS = 60_000; // refresh 60s before exp

function decodeJwtExp(token: string): number | null {
  try {
    const payload = JSON.parse(atob(token.split('.')[1] ?? ''));
    return typeof payload.exp === 'number' ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

let refreshInFlight: Promise<void> | null = null;

async function refreshTokens(): Promise<void> {
  const refresh = tokenStorage.getRefresh();
  if (!refresh) throw new AuthException('No refresh token');
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async () => {
    try {
      const res = await axios.post(
        `${BASE_URL}/api/auth/refresh-token`,
        { refreshToken: refresh },
        { headers: { 'Content-Type': 'application/json' } }
      );
      const data = res.data?.data ?? res.data;
      const access = data?.access_token ?? data?.accessToken;
      const newRefresh = data?.refresh_token ?? data?.refreshToken ?? refresh;
      if (!access) throw new AuthException('Refresh response missing access_token');
      tokenStorage.set(access, newRefresh);
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
}

function mapError(err: AxiosError): never {
  if (err.code === 'ERR_NETWORK' || err.message === 'Network Error') {
    throw new NetworkException(err.message);
  }
  const status = err.response?.status ?? 0;
  const data: any = err.response?.data ?? {};
  const message =
    data?.error?.message ?? data?.message ?? err.message ?? 'Request failed';

  if (status === 422 || data?.errors) {
    throw new ValidationException(message, data.errors ?? {}, data);
  }
  if (status === 401 || status === 403) {
    throw new AuthException(message, status, data);
  }
  if (status >= 500) {
    throw new ServerException(message, status, data);
  }
  throw new ApiException(message, status, { code: data?.error?.code, raw: data });
}

export const api: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  timeout: 30_000,
  headers: { 'Content-Type': 'application/json' },
});

// ── Request: attach JWT, refresh proactively ───────────────────────
api.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
  const token = tokenStorage.getAccess();
  if (!token) return config;

  const exp = decodeJwtExp(token);
  const isAuthEndpoint = config.url?.startsWith('/api/auth/');
  if (exp && exp - Date.now() < REFRESH_LEAD_MS && !isAuthEndpoint) {
    try {
      await refreshTokens();
    } catch {
      // fall through — request will hit 401 and be retried below
    }
  }
  const fresh = tokenStorage.getAccess();
  if (fresh) {
    config.headers.set('Authorization', `Bearer ${fresh}`);
  }
  return config;
});

// ── Response: 401 retry once, then map errors ──────────────────────
api.interceptors.response.use(
  (r) => r,
  async (err: AxiosError) => {
    const original = err.config as (AxiosRequestConfig & { _retried?: boolean }) | undefined;
    const status = err.response?.status;
    if (
      status === 401 &&
      original &&
      !original._retried &&
      !original.url?.startsWith('/api/auth/refresh-token')
    ) {
      original._retried = true;
      try {
        await refreshTokens();
        const fresh = tokenStorage.getAccess();
        if (fresh && original.headers) {
          (original.headers as Record<string, string>).Authorization = `Bearer ${fresh}`;
        }
        return api.request(original);
      } catch {
        tokenStorage.clear();
      }
    }
    mapError(err);
  }
);
